import { JoinStep } from '~/types/join'
import type { CheckoutTrackingParams } from '~/utils/checkoutTracking'
import { buildCheckoutParams } from '~/utils/checkoutTracking'
import { UserRole } from '~/types/account'

/**
 * GA4 funnel tracking for the Join process (AB#64104).
 *
 * Join reuses the GA4 Checkout Tracking schema rather than defining its own events: the
 * event names (`checkout_step_view` / `checkout_step_completed`) and the eight custom
 * params are exactly what `utils/checkoutTracking.ts` already emits for checkout, so the
 * analyst gets one funnel shape across both flows. What is unique to Join is the set of
 * `page_section` values below.
 *
 * The section names follow the checkout sections' convention — snake_case, named for the
 * thing the applicant is being asked for rather than for the route or the component — with a
 * `join_` prefix so a funnel report can separate the two flows on `page_section` alone
 * without needing a second param. They are derived from the JoinStep enum, which is the
 * flow's own definition of its steps, so a step cannot exist without a section: the
 * JOIN_STEP_TO_PAGE_SECTION record is exhaustive over the enum and a spec asserts that.
 *
 * `join_personal_details` deliberately does not mirror its route (`/join/my-details`): the
 * routes are shopper-facing copy and have been renamed before, while a section name is a
 * historical key in GA4 that cannot be changed without breaking continuity in the reports.
 */
export type JoinPageSection =
  | 'join_entry'
  | 'join_welcome_back'
  | 'join_registration'
  | 'join_select_offer'
  | 'join_team_leader'
  | 'join_agreements'
  | 'join_personal_details'
  | 'join_vat'
  | 'join_payment'
  | 'join_confirmation'

/**
 * JoinStep -> page_section.
 *
 * Exhaustive over the enum, so adding a step without giving it a section will not compile.
 * The pages name their own section directly rather than looking it up here: registration and
 * confirmation are sections with no JoinStep of their own, so a lookup could only ever cover
 * some of the call sites. This record is the contract between the enum and the section names.
 */
export const JOIN_STEP_TO_PAGE_SECTION: Record<JoinStep, JoinPageSection> = {
  [JoinStep.Entry]: 'join_entry',
  [JoinStep.WelcomeBack]: 'join_welcome_back',
  [JoinStep.SelectOffer]: 'join_select_offer',
  [JoinStep.TeamLeader]: 'join_team_leader',
  [JoinStep.Agreements]: 'join_agreements',
  [JoinStep.MyDetails]: 'join_personal_details',
  [JoinStep.Vat]: 'join_vat',
  [JoinStep.Payment]: 'join_payment'
}

/**
 * The eight schema params for a Join applicant.
 *
 * Reuses buildCheckoutParams so Join cannot drift from checkout on the shared params.
 * Identity comes from the role the same way checkout derives it, so a signed-in recruit
 * reports as a customer and an agent completing a join for someone else reports as a
 * demonstrator.
 *
 * cart_type / order_type are derived from that same role rather than from a table of sections.
 * The recruit cart is created by verifyCustomer(), which is also what sets UserRole.Recruit —
 * so the role IS the signal that a cart exists, and the two can never disagree. The kit is
 * seeded there, read back by fetchRecruit() from `r.cart.items`, and finally ordered through
 * checkoutStore.submitOrder().
 *
 * A section table got this wrong in both directions: it reported 'none' on the registration
 * COMPLETION, which fires after verifyCustomer() has already provisioned the cart, while
 * needing a second place to keep in step with the flow. Reading the role means the registration
 * view (no cart yet) and its completion (cart now exists) each report themselves correctly with
 * no list to maintain.
 *
 * Both sides use existing members of the schema's unions: a recruit cart holding the kit is an
 * ordinary cart, and the applicant buying it is a customer at that point.
 */
export function buildJoinParams (role: UserRole | undefined, path: string): CheckoutTrackingParams {
  const hasRecruitCart = role === UserRole.Recruit
  return buildCheckoutParams({
    role,
    path,
    orderType: hasRecruitCart ? 'customer_order' : 'none',
    cartType: hasRecruitCart ? 'standard' : 'none',
    isGift: false
  })
}
